import type { OrganizationDto } from "@intelligems/ig-types/dtos/organizations";

const MARKER_STORAGE_KEY = "ig_ads_click";
const REPORTED_STORAGE_KEY = "ig_ads_install_reported_org_ids";

export const ADS_CLICK_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export const CLOCK_SKEW_TOLERANCE_MS = 15 * 60 * 1000;

const CLICK_ID_TYPES = ["gclid", "gbraid", "wbraid"] as const;
export type ClickIdType = (typeof CLICK_ID_TYPES)[number];

export interface AdsClickMarker {
  capturedAtTs: number;
  clickIdType: ClickIdType;
  verified: boolean;
}

const isMarker = (value: unknown): value is AdsClickMarker =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as AdsClickMarker).capturedAtTs === "number" &&
  typeof (value as AdsClickMarker).verified === "boolean" &&
  CLICK_ID_TYPES.includes((value as AdsClickMarker).clickIdType);

export const readAdsClickMarker = (nowTs: number): AdsClickMarker | null => {
  try {
    const raw = window.localStorage.getItem(MARKER_STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);

    if (!isMarker(parsed)) {
      return null;
    }

    if (nowTs - parsed.capturedAtTs > ADS_CLICK_TTL_MS) {
      return null;
    }

    // Both timestamps are this browser's clock, so this only fires when the
    // clock moved backwards between the landing page and now. Cheap to reject:
    // a marker dated in the future would otherwise sail past the expiry check
    // above and satisfy the click-precedes-install comparison for free.
    if (parsed.capturedAtTs - nowTs > CLOCK_SKEW_TOLERANCE_MS) {
      return null;
    }

    return parsed;
  } catch {
    // Safari private browsing throws on access.
    return null;
  }
};

export const clearAdsClickMarker = (): void => {
  try {
    window.localStorage.removeItem(MARKER_STORAGE_KEY);
  } catch {
    // Non-fatal: the reported-org list already makes reporting idempotent.
  }
};

export const readReportedOrgIds = (): string[] => {
  try {
    const raw = window.localStorage.getItem(REPORTED_STORAGE_KEY);
    if (raw === null) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    return [];
  }
};

export const recordReportedOrgId = (orgId: string): void => {
  try {
    const next = readReportedOrgIds();
    if (next.includes(orgId)) {
      return;
    }
    next.push(orgId);
    window.localStorage.setItem(REPORTED_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // A failed write means we may report this org again on a later page load.
    // Google discards duplicates carrying the same transaction_id, so the
    // account stays correct either way.
  }
};

export type InstallConversionOrg = Pick<
  OrganizationDto,
  "id" | "installCount" | "createdAtTs"
>;

export const shouldReportInstallConversion = (input: {
  marker: AdsClickMarker | null;
  hasClickCookie: boolean;
  org: InstallConversionOrg;
  alreadyReportedOrgIds: string[];
  nowTs: number;
}): boolean => {
  const { marker, hasClickCookie, org, alreadyReportedOrgIds, nowTs } = input;

  if (!marker) {
    return false;
  }
  if (!marker.verified) {
    return false;
  }
  if (!hasClickCookie) {
    return false;
  }
  if (org.installCount !== 1) {
    return false;
  }
  if (nowTs - org.createdAtTs > ADS_CLICK_TTL_MS) {
    return false;
  }
  if (marker.capturedAtTs > org.createdAtTs + CLOCK_SKEW_TOLERANCE_MS) {
    return false;
  }
  if (alreadyReportedOrgIds.includes(org.id)) {
    return false;
  }

  return true;
};
